import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { GridColDef } from "@mui/x-data-grid";
import DataGrid from "../../../components/common/CustomeDataGrid";
import Button from "../../../components/common/Button";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import type { RootState } from "../../../store/Index";
import { useAppDispatch } from "../../../store/hooks";
import { fetchGroups } from "../../../store/slice/rolesAndPermissions/thunks";
import apiService from "../../../service/Api";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

type PermissionAction = "view" | "add" | "edit" | "delete";

// Django permission verb for each column (edit maps to Django's "change").
const PERMISSION_VERB: Record<PermissionAction, string> = {
  view: "view",
  add: "add",
  edit: "change",
  delete: "delete",
};

interface ModuleRow {
  id: string;
  module: string;
  app_label: string;
  model: string;
}

export default function RoleManagementTab() {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();
  const canManageRoles = hasPermission(PERMISSIONS.USER_MANAGEMENT_DELETE);

  const { contentTypes, groups, permissions } = useSelector(
    (state: RootState) => state.rolesAndPermissions,
  );

  const [selectedGroupId, setSelectedGroupId] = useState<number | "">("");
  const [checkedPermIds, setCheckedPermIds] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  // Lookup: "app_label.verb_model" -> permission id
  const permIdByString = useMemo(() => {
    const map = new Map<string, number>();
    permissions.forEach((p) => {
      const key =
        p.full_codename ||
        `${p.content_type.app_label}.${p.codename}`;
      map.set(key, p.id);
    });
    return map;
  }, [permissions]);

  // Seed the editable selection whenever the chosen role changes.
  useEffect(() => {
    if (selectedGroupId === "") {
      setCheckedPermIds(new Set());
      return;
    }
    const group = groups.find((g) => g.id === selectedGroupId);
    setCheckedPermIds(new Set(group?.permissions ?? []));
  }, [selectedGroupId, groups]);

  const permStringFor = (row: ModuleRow, action: PermissionAction) =>
    `${row.app_label}.${PERMISSION_VERB[action]}_${row.model}`;

  const togglePermission = (row: ModuleRow, action: PermissionAction) => {
    const permId = permIdByString.get(permStringFor(row, action));
    if (permId == null) return;
    setCheckedPermIds((prev) => {
      const next = new Set(prev);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return next;
    });
  };

  const rows: ModuleRow[] = useMemo(() => {
    if (!Array.isArray(contentTypes)) return [];
    return contentTypes.map((ct) => ({
      id: `${ct.app_label} / ${ct.model}`,
      module: `${ct.app_label} / ${ct.model}`,
      app_label: ct.app_label,
      model: ct.model,
    }));
  }, [contentTypes]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.module.toLowerCase().includes(term));
  }, [rows, search]);

  const renderPermissionCell = (row: ModuleRow, action: PermissionAction) => {
    const permId = permIdByString.get(permStringFor(row, action));
    // Permission does not exist for this model/verb -> nothing to grant.
    if (permId == null) return <Checkbox checked={false} disabled />;
    return (
      <Checkbox
        checked={checkedPermIds.has(permId)}
        disabled={!canManageRoles}
        onChange={() => togglePermission(row, action)}
      />
    );
  };

  const columns: GridColDef<ModuleRow>[] = [
    { field: "module", headerName: "Module", flex: 1.5, sortable: true },
    {
      field: "view",
      headerName: "View",
      width: 90,
      sortable: false,
      renderCell: (params) => renderPermissionCell(params.row, "view"),
    },
    {
      field: "add",
      headerName: "Add",
      width: 90,
      sortable: false,
      renderCell: (params) => renderPermissionCell(params.row, "add"),
    },
    {
      field: "edit",
      headerName: "Edit",
      width: 90,
      sortable: false,
      renderCell: (params) => renderPermissionCell(params.row, "edit"),
    },
    {
      field: "delete",
      headerName: "Delete",
      width: 90,
      sortable: false,
      renderCell: (params) => renderPermissionCell(params.row, "delete"),
    },
  ];

  const handleSave = async () => {
    if (selectedGroupId === "") return;
    try {
      setSaving(true);
      await apiService.patch(
        `/api/authentication/groups/${selectedGroupId}/`,
        { permissions: Array.from(checkedPermIds) },
      );
      toast.success("Role permissions updated successfully");
      dispatch(fetchGroups());
    } catch (err) {
      const errorDetail = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      toast.error(errorDetail || "Failed to update role permissions");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Role</InputLabel>
          <Select
            label="Role"
            value={selectedGroupId}
            onChange={(e) => setSelectedGroupId(e.target.value as number)}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search permissions by module..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <SearchIcon sx={{ mr: 1, color: "text.disabled" }} />
            ),
          }}
          sx={{ minWidth: 300 }}
          disabled={selectedGroupId === ""}
        />

        <Box sx={{ flexGrow: 1 }} />

        {canManageRoles && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={selectedGroupId === "" || saving}
          >
            {saving ? "Saving..." : "Save Permissions"}
          </Button>
        )}
      </Box>

      {selectedGroupId === "" ? (
        <Alert severity="info">
          Select a role above to view and edit its permissions.
        </Alert>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Toggle the permissions granted to this role. Every user assigned to
            the role inherits these permissions.
          </Typography>
          <div style={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { pageSize: 25, page: 0 } },
              }}
              disableRowSelectionOnClick
              density="compact"
            />
          </div>
        </Paper>
      )}
    </Box>
  );
}
